<script>
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';

export default {
  components: {
    NextButton,
    Avatar,
  },
  props: {
    agentList: {
      type: Array,
      default: () => [],
    },
    selectedAgents: {
      type: Array,
      default: () => [],
    },
    updateSelectedAgents: {
      type: Function,
      default: () => {},
    },
    // NOTICE: KAPTION-INTEGRATION — Secure Assignment Mode leader selection
    // (group-visibility.LEADER.1, Screen 3 Step 2). The column renders only for
    // restricted teams while KAPTION_GROUP_VISIBILITY is available.
    showLeaderColumn: {
      type: Boolean,
      default: false,
    },
    selectedLeaders: {
      type: Array,
      default: () => [],
    },
    updateSelectedLeaders: {
      type: Function,
      default: () => {},
    },
    isWorking: {
      type: Boolean,
      default: false,
    },
    submitButtonText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {};
  },
  computed: {
    selectedAgentCount() {
      return this.selectedAgents.length;
    },
    allAgentsSelected() {
      return this.selectedAgents.length === this.agentList.length;
    },
    // NOTICE: KAPTION-INTEGRATION — a restricted team needs ≥1 leader before
    // Secure Assignment Mode can be enabled (group-visibility.PREFLIGHT.1); surface it
    // here as a soft warning while selecting agents.
    needsLeader() {
      return (
        this.showLeaderColumn &&
        this.selectedAgentCount > 0 &&
        this.selectedLeaders.length === 0
      );
    },
    disableSubmitButton() {
      return this.selectedAgentCount === 0;
    },
  },
  methods: {
    isAgentSelected(agentId) {
      return this.selectedAgents.includes(agentId);
    },
    handleSelectAgent(agentId) {
      const shouldRemove = this.isAgentSelected(agentId);

      let result = [];
      if (shouldRemove) {
        result = this.selectedAgents.filter(item => item !== agentId);
        // NOTICE: KAPTION-INTEGRATION — deselecting a member also drops leadership
        if (this.isAgentLeader(agentId)) {
          this.updateSelectedLeaders(
            this.selectedLeaders.filter(item => item !== agentId)
          );
        }
      } else {
        result = [...this.selectedAgents, agentId];
      }

      this.updateSelectedAgents(result);
    },
    // NOTICE: KAPTION-INTEGRATION
    isAgentLeader(agentId) {
      return this.selectedLeaders.includes(agentId);
    },
    // NOTICE: KAPTION-INTEGRATION
    handleToggleLeader(agentId) {
      if (!this.isAgentSelected(agentId)) return;

      const result = this.isAgentLeader(agentId)
        ? this.selectedLeaders.filter(item => item !== agentId)
        : [...this.selectedLeaders, agentId];
      this.updateSelectedLeaders(result);
    },
    selectAllAgents() {
      const result = this.agentList.map(item => item.id);
      this.updateSelectedAgents(result);
    },
    agentRowClass(agentId) {
      return { 'is-active': this.isAgentSelected(agentId) };
    },
  },
};
</script>

<template>
  <div>
    <div class="add-agents__header" />
    <table>
      <thead
        class="[&>th]:font-semibold [&>th]:tracking-[1px] ltr:[&>th]:text-left rtl:[&>th]:text-right [&>th]:px-2.5 [&>th]:uppercase [&>th]:text-n-slate-12"
      >
        <tr>
          <td class="ltr:pl-2.5 rtl:pr-2.5">
            <div class="flex items-center">
              <input
                name="select-all-agents"
                type="checkbox"
                :checked="allAgentsSelected ? 'checked' : ''"
                :title="$t('TEAMS_SETTINGS.AGENTS.SELECT_ALL')"
                @click.self="selectAllAgents"
              />
            </div>
          </td>
          <td class="text-n-slate-12 ltr:pl-2.5 rtl:pr-2.5">
            {{ $t('TEAMS_SETTINGS.AGENTS.AGENT') }}
          </td>
          <td class="text-n-slate-12 ltr:pl-2.5 rtl:pr-2.5">
            {{ $t('TEAMS_SETTINGS.AGENTS.EMAIL') }}
          </td>
          <!-- NOTICE: KAPTION-INTEGRATION — Secure Assignment leader column -->
          <td
            v-if="showLeaderColumn"
            class="text-n-slate-12 ltr:pl-2.5 rtl:pr-2.5"
          >
            {{ $t('TEAMS_SETTINGS.AGENTS.LEADER') }}
          </td>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="agent in agentList"
          :key="agent.id"
          :class="agentRowClass(agent.id)"
          class="border-b border-n-weak [&>td]:p-2.5 [&>td]:text-n-slate-12"
        >
          <td class="w-12">
            <div class="flex items-center">
              <input
                type="checkbox"
                :checked="isAgentSelected(agent.id)"
                @click.self="() => handleSelectAgent(agent.id)"
              />
            </div>
          </td>
          <td>
            <div class="flex items-center gap-2">
              <Avatar
                :src="agent.thumbnail"
                :name="agent.name"
                :status="agent.availability_status"
                :size="24"
                hide-offline-status
                rounded-full
              />
              <h4 class="text-base mb-0 text-n-slate-12">
                {{ agent.name }}
              </h4>
            </div>
          </td>
          <td>
            {{ agent.email || '---' }}
          </td>
          <!-- NOTICE: KAPTION-INTEGRATION — Secure Assignment leader toggle -->
          <td v-if="showLeaderColumn" class="w-16">
            <div class="flex items-center">
              <input
                type="checkbox"
                name="team-leader"
                :checked="isAgentLeader(agent.id)"
                :disabled="!isAgentSelected(agent.id)"
                :title="$t('TEAMS_SETTINGS.AGENTS.LEADER')"
                @click.self="() => handleToggleLeader(agent.id)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <!-- NOTICE: KAPTION-INTEGRATION — soft leader warning (Screen 3 Step 2) -->
    <p
      v-if="needsLeader"
      class="mt-2 text-sm text-n-amber-11"
      data-testid="needs-leader-warning"
    >
      {{ $t('TEAMS_SETTINGS.AGENTS.NEEDS_LEADER') }}
    </p>
    <div class="flex items-center justify-between mt-2">
      <p>
        {{
          $t('TEAMS_SETTINGS.AGENTS.SELECTED_COUNT', {
            selected: selectedAgents.length,
            total: agentList.length,
          })
        }}
      </p>
      <NextButton
        type="submit"
        :label="submitButtonText"
        :disabled="disableSubmitButton"
        :is-loading="isWorking"
      />
    </div>
  </div>
</template>

<style scoped>
input {
  @apply mb-0;
}
</style>
